import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { InquiryShell } from "./InquiryShell";
import { track } from "@/lib/analytics";
import { buildInquiryFormData } from "./submit-inquiry";
import { useInquirySubmit } from "./useInquirySubmit";
import { useTechRiderParser } from "./useTechRiderParser";
import { buildSummaryRows, EventSummaryPanel } from "./EventSummaryPanel";
import { EstimateReviewGate } from "./EstimateReviewGate";
import { requestEstimate, type EstimateState } from "./estimate-api";
import { TechRiderStep } from "./TechRiderStep";
import { EventDetailsStep } from "./EventDetailsStep";
import { ContactStep } from "./ContactStep";
import { VenueStep } from "./VenueStep";
import { ContentStep } from "./ContentStep";
import { InquirySuccess } from "./InquirySuccess";
import { type PackageFormData, type Step } from "./types";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import {
  getActiveInquiryFields,
} from "./profile-state";
import { firstInvalidStep } from "./validators";
import styles from "./SoundgearInquiry.module.css";

/**
 * Multi-step sound gear inquiry wizard.
 *
 * Ported from the legacy Next.js soundgear inquiry wizard for the Bun +
 * React Router build.
 *  - next/navigation useRouter -> react-router useNavigate.
 *  - Suspense wrapper (legacy page.tsx) dropped — there is no async route
 *    boundary in the SPA build.
 *  - The dev-only `fillTestData` window listener was deliberately not
 *    ported; it was a Next.js dev-server affordance.
 *  - fetch('/api/inquiry') -> postInquiry (submit-inquiry.ts), which owns
 *    the multipart payload rules and goes through the shared apiFetch.
 *
 * Wizard state (current step + accumulated PackageFormData) lives entirely
 * in local component state — there is no URL-carried step. Back/Continue
 * mutate `step`; the form object is never reset between steps.
 */
interface SoundgearInquiryProps {
  profile: InquiryProfileConfig;
  onBackToEntry?: () => void;
}

export function SoundgearInquiry({
  profile,
  onBackToEntry,
}: SoundgearInquiryProps): React.JSX.Element {
  const navigate = useNavigate();
  const stepIds = profile.steps.map((configuredStep) => configuredStep.id);
  // No persistence: every load starts a fresh form. A page refresh resets
  // the wizard to its first step with the profile defaults.
  const [step, setStep] = useState<Step>(() => profile.initialStep);
  const [showValidation, setShowValidation] = useState(false);
  const [formData, setFormData] = useState<Partial<PackageFormData>>(() => ({
    ...profile.initialFormData,
  }));
  // Set of field names whose date / time picker last saw unparseable input.
  const [invalidFields, setInvalidFields] = useState<Set<string>>(() => new Set());

  const setFieldValidity = (field: string, valid: boolean): void => {
    setInvalidFields((prev) => {
      const had = prev.has(field);
      if (valid && had) {
        const next = new Set(prev);
        next.delete(field);
        return next;
      }
      if (!valid && !had) {
        const next = new Set(prev);
        next.add(field);
        return next;
      }
      return prev;
    });
  };

  useEffect(() => {
    track("step_viewed", { profileKey: profile.key, step, path: "full_brief" });
  }, [step, profile.key]);

  const techRider = useTechRiderParser(profile);
  // Extra attachments beyond the parsed primary document (#168) — stored for
  // the operator, never parsed.
  const [additionalDocuments, setAdditionalDocuments] = useState<File[]>([]);

  const inquirySubmit = useInquirySubmit({
    profileKey: profile.key,
    path: "full_brief",
    // parsedTechRiderJson is the TechRiderRequirements the document parse
    // returned. A pack profile's answers reach the prompt via profileAnswers
    // regardless; this field carries only the technical extraction.
    build: (submissionKey) =>
      buildInquiryFormData({
        profile,
        formData,
        techRiderFile: techRider.file,
        additionalDocuments,
        parsedTechRider: techRider.getParsed()?.rider ?? null,
        submissionKey,
      }),
  });
  const activeFields = useMemo(
    () => getActiveInquiryFields(profile, formData),
    [profile, formData],
  );

  // Every change to the draft goes through here, a typed answer and the
  // document parse's merge alike. Nothing is pruned: which answers are
  // asked, checked, shown and sent is decided on the way out, by one rule
  // that knows whether a document is attached (active-questions.ts).
  const riderAttached = techRider.file !== null;
  const patchFormData = (patch: Partial<PackageFormData>): void => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const updateField = <K extends keyof PackageFormData>(
    field: K,
    value: PackageFormData[K],
  ): void => {
    patchFormData({ [field]: value } as Partial<PackageFormData>);
    // Reveal-then-live: once validation is revealed it stays on. Each step's
    // error map recomputes per render, so a field's message clears the moment
    // it becomes valid — no need to hide every message on the first keystroke.
  };

  // Which page of the content step is showing. Pages are a rendering
  // decision — the pack is one list, four to a page (ContentStep) — so
  // the page is not a Step: it resets with every step change, and the
  // stepper and the summary panel know one Content step, which a jump
  // lands on the top of.
  const [contentPage, setContentPage] = useState(0);

  const goToStep = (next: Step): void => {
    setShowValidation(false);
    setContentPage(0);
    setStep(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const goToContentPage = (next: number): void => {
    setShowValidation(false);
    setContentPage(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const getAdjacentStep = (current: Step, offset: -1 | 1): Step => {
    const index = stepIds.indexOf(current);
    if (index < 0) return current;
    return stepIds[index + offset] ?? current;
  };

  const goToNextStep = (current: Step): void => {
    track("step_completed", {
      profileKey: profile.key,
      step: current,
      path: "full_brief",
    });
    goToStep(getAdjacentStep(current, 1));
  };

  // "Continue to venue" beats "Continue" — the customer always knows what's
  // next. The wizard owns this because only it knows the step order.
  const continueLabelFor = (current: Step): string => {
    const next = profile.steps[stepIds.indexOf(current) + 1];
    return next ? `Continue to ${next.progressLabel.toLowerCase()}` : "Continue";
  };

  const goToPreviousStep = (current: Step): void => {
    goToStep(getAdjacentStep(current, -1));
  };

  // Reveal-then-live plus "take me to the problem": scroll/focus the first
  // errored field (stable inquiry-field-<name> ids, FormField). Instant
  // scroll — no motion (DESIGN.md).
  const revealValidationErrors = (errorFields: readonly string[]): void => {
    setShowValidation(true);
    if (errorFields.length === 0) return;
    track("field_error_shown", {
      profileKey: profile.key,
      step,
      path: "full_brief",
      fieldNames: errorFields,
    });
    const el = document.getElementById(`inquiry-field-${errorFields[0]}`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "instant", block: "center" });
      el.focus({ preventScroll: true });
    }
  };

  const handleCancel = (): void => {
    if (onBackToEntry) {
      onBackToEntry();
      return;
    }
    navigate("/");
  };

  // Dev-only test-data filler — visible only on localhost. Fills every form
  // field and jumps to the final step so QA can one-click to submit. The
  // tech-rider file (separate state) is untouched, so a manually attached
  // file still rides along with the submission.
  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  const fillTestData = (): void => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    setFormData({
      eventType: profile.eventDetailsStep.eventTypeOptions[0]?.value,
      eventName: "Test Wedding Reception",
      organization: "Test Corp",
      eventDate: d.toISOString().split("T")[0],
      eventStartTime: "6:00 PM",
      eventEndTime: "11:00 PM",
      attendance: 150,
      profileAnswers: { hasSpeeches: true },
      location: "123 Test Venue, Wellington",
      venueContact: "Venue Manager 021 555 1234",
      indoorOutdoor: "Indoor",
      hasStage: true,
      stageDetails: "4m x 3m stage",
      siteAvailableFrom: "2:00 PM",
      packOutTime: "12:00 AM",
      contactName: "James Huddon",
      contactEmail: "relahunter@gmail.com",
      contactPhone: "123456789",
      details: "Test booking - please ignore",
    });
    setShowValidation(false);
    setStep(profile.steps[profile.steps.length - 1]?.id ?? profile.initialStep);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // The parse applies here — before the Event step renders — so a run
  // sheet's date/venue/attendance pre-fill the steps that ask for them
  // instead of contradicting what the customer would have typed.
  const handleTechRiderContinue = async (): Promise<void> => {
    goToNextStep("tech-rider");
    const updates = await techRider.applyIfReady(formData);
    if (updates) patchFormData(updates);
  };

  // ─── Estimate gate + summary panel ────────────────────────────────────
  // The estimate is asked for once, when the Customer leaves Content: the
  // server has the model choose the gear and prices it, so it is a model
  // call and nothing asks while they type. The gate shows while it is
  // worked out and then shows the range; with no range (off, failed,
  // nothing to price) the wizard goes straight on to Contact — the
  // estimate is never a blocker. The panel beside the steps is the
  // answers alone.
  const estimateEnabled = profile.estimate?.enabled === true;
  const [estimateState, setEstimateState] = useState<EstimateState>({
    status: "idle",
  });
  const estimateRunRef = useRef(0);
  const showEstimateGate = estimateState.status !== "idle";

  const closeEstimateGate = (): void => {
    estimateRunRef.current += 1;
    setEstimateState({ status: "idle" });
  };

  // Panel rows can jump while the gate is up — leaving the gate then is the
  // same decision as "Tweak my answers".
  const handlePanelJump = (target: Step): void => {
    closeEstimateGate();
    goToStep(target);
  };

  const handleContentContinue = (): void => {
    if (!estimateEnabled) {
      goToNextStep("content");
      return;
    }
    track("step_completed", {
      profileKey: profile.key,
      step: "content",
      path: "full_brief",
    });
    const runId = estimateRunRef.current + 1;
    estimateRunRef.current = runId;
    setEstimateState({ status: "loading" });
    window.scrollTo({ top: 0, behavior: "instant" });
    void requestEstimate(profile, formData, riderAttached).then((range) => {
      if (estimateRunRef.current !== runId) return;
      if (!range) {
        setEstimateState({ status: "idle" });
        goToStep("contact");
        return;
      }
      track("estimate_shown", { profileKey: profile.key, path: "full_brief" });
      track("estimate_review_shown", {
        profileKey: profile.key,
        path: "full_brief",
      });
      setEstimateState({ status: "ready", range });
    });
  };

  const handleEstimateGateContinue = (): void => {
    track("estimate_review_continued", {
      profileKey: profile.key,
      path: "full_brief",
    });
    closeEstimateGate();
    goToStep("contact");
  };

  const handleEstimateGateAdjust = (): void => {
    track("estimate_review_abandoned", {
      profileKey: profile.key,
      path: "full_brief",
    });
    closeEstimateGate();
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Safety net: applyIfReady is a no-op once applied, so this only fires
  // when the parse was still in flight while the customer sped through the
  // Event step — late answers still only fill gaps, never overwrite.
  const handleEventDetailsContinue = async (): Promise<void> => {
    goToNextStep("event-details");
    const updates = await techRider.applyIfReady(formData);
    if (updates) patchFormData(updates);
  };

  // Sending is gated on every step, not only the one Send sits on: a jump
  // from the summary panel can land on Contact past a step never finished.
  // The first such step opens with its errors shown.
  const handleSend = (): void => {
    const unfinished = firstInvalidStep(profile, formData, riderAttached);
    if (unfinished && unfinished !== step) {
      goToStep(unfinished);
      setShowValidation(true);
      return;
    }
    void inquirySubmit.submit();
  };

  if (inquirySubmit.state.status === "success") {
    return (
      <InquirySuccess
        profile={profile}
        path="full_brief"
        statusToken={inquirySubmit.state.statusToken}
        title="Thank you"
        staticSubtitle="We'll be in touch within 24 hours."
      />
    );
  }

  const isSubmitting = inquirySubmit.isSubmitting;
  const submitError = inquirySubmit.submitError;
  const progressIndex = stepIds.indexOf(step);
  const showProgress = progressIndex >= 0;
  const summaryRows = buildSummaryRows(formData, profile, riderAttached);

  return (
    <InquiryShell
      company={
        profile.company
          ? { name: profile.company.name, url: profile.company.publicUrl }
          : undefined
      }
      aside={
        // Before any answer the panel is empty — don't reserve the rail,
        // let the step column center full-width.
        summaryRows.some((row) => row.value) ? (
          <EventSummaryPanel
            rows={summaryRows}
            currentStep={step}
            onJump={handlePanelJump}
          />
        ) : undefined
      }
    >
      {techRider.isApplying && (
        <div className={styles.parsingOverlay}>
          <div className={styles.parsingSpinner} />
          <p className={styles.parsingTitle}>Loading your files...</p>
          <p className={styles.parsingNote}>This can take 10-20 seconds</p>
        </div>
      )}

      <div className={styles.flow}>
        {isLocalhost && (
          <button
            type="button"
            onClick={fillTestData}
            className={styles.fillTestData}
          >
            Fill test data (dev)
          </button>
        )}
        {showProgress && (
          <div className={styles.progress}>
            {/* One continuous track; the fill's width slides to the current
                step — the sanctioned stepper motion (DESIGN.md). */}
            <div className={styles.progressTrack}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${
                    ((showEstimateGate
                      ? profile.steps.length
                      : progressIndex + 1) /
                      profile.steps.length) *
                    100
                  }%`,
                }}
              />
            </div>
            <div
              className={styles.progressLabels}
              role="list"
              aria-label={`${profile.label} enquiry progress`}
            >
              {profile.steps.map((configuredStep, i) => {
                const isDone = i < progressIndex && !showEstimateGate;
                const isCurrent =
                  configuredStep.id === step && !showEstimateGate;
                const labelClass = isCurrent
                  ? styles.stepCurrent
                  : isDone
                    ? styles.stepDone
                    : styles.stepUpcoming;
                return (
                  <div
                    key={configuredStep.id}
                    role="listitem"
                    aria-label={configuredStep.progressLabel}
                    aria-current={isCurrent ? "step" : undefined}
                    className={styles.progressStep}
                  >
                    {isDone ? (
                      // Completed steps are revisitable — the label is the
                      // jump-back affordance.
                      <button
                        type="button"
                        onClick={() => goToStep(configuredStep.id)}
                        className={`${styles.progressLabelButton} ${labelClass}`}
                      >
                        {configuredStep.progressLabel}
                      </button>
                    ) : (
                      <span className={`${styles.progressLabel} ${labelClass}`}>
                        {configuredStep.progressLabel}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showEstimateGate ? (
          <EstimateReviewGate
            range={
              estimateState.status === "ready" ? estimateState.range : null
            }
            companyName={profile.company?.name}
            onContinue={handleEstimateGateContinue}
            onAdjust={handleEstimateGateAdjust}
          />
        ) : (
        <>
        {step === "tech-rider" && (
          <TechRiderStep
            profile={profile}
            file={techRider.file}
            fileError={techRider.fileError}
            parseFailed={techRider.parseFailed}
            onFileChange={techRider.setFile}
            additionalFiles={additionalDocuments}
            onAddAdditionalFiles={(files) =>
              setAdditionalDocuments((prev) => [...prev, ...files])
            }
            onRemoveAdditionalFile={(index) =>
              setAdditionalDocuments((prev) =>
                prev.filter((_, i) => i !== index),
              )
            }
            onCancel={handleCancel}
            backLabel={onBackToEntry ? "Back to options" : "Cancel"}
            onContinue={() => {
              void handleTechRiderContinue();
            }}
            continueLabel={continueLabelFor("tech-rider")}
            continueDisabled={techRider.isApplying}
          />
        )}

        {step === "event-details" && (
          <EventDetailsStep
            profile={profile}
            formData={formData}
            activeFields={activeFields}
            showValidation={showValidation}
            autofilledFields={techRider.autofilledFields}
            invalidFields={invalidFields}
            setFieldValidity={setFieldValidity}
            updateField={updateField}
            onBack={() => goToPreviousStep("event-details")}
            onContinue={() => {
              void handleEventDetailsContinue();
            }}
            continueLabel={continueLabelFor("event-details")}
            onShowValidation={revealValidationErrors}
          />
        )}

        {step === "contact" && (
          <ContactStep
            profile={profile}
            formData={formData}
            showValidation={showValidation}
            updateField={updateField}
            submitError={submitError}
            isSubmitting={isSubmitting}
            onBack={() => goToPreviousStep("contact")}
            onContinue={handleSend}
            onShowValidation={revealValidationErrors}
          />
        )}

        {step === "venue" && (
          <VenueStep
            profile={profile}
            formData={formData}
            activeFields={activeFields}
            showValidation={showValidation}
            autofilledFields={techRider.autofilledFields}
            invalidFields={invalidFields}
            setFieldValidity={setFieldValidity}
            updateField={updateField}
            onBack={() => goToPreviousStep("venue")}
            onContinue={() => goToNextStep("venue")}
            continueLabel={continueLabelFor("venue")}
            onShowValidation={revealValidationErrors}
          />
        )}

        {step === "content" && (
          <ContentStep
            profile={profile}
            formData={formData}
            autofilledFields={techRider.autofilledFields}
            hasTechRiderFile={riderAttached}
            showValidation={showValidation}
            updateField={updateField}
            onBack={() => goToPreviousStep("content")}
            onContinue={handleContentContinue}
            onShowValidation={revealValidationErrors}
            continueLabel={
              estimateEnabled ? "See my estimate" : continueLabelFor("content")
            }
            page={contentPage}
            onPageChange={goToContentPage}
          />
        )}
        </>
        )}
      </div>
    </InquiryShell>
  );
}
